import { scheduleService } from '@/services/schedule.service';
import type { EventRow } from '@/lib/schedule';
import { seriesMayHaveFollowing } from '@/lib/recurrence';
import type { SeriesDeleteAction } from '@/pages/review/ItemFormModal';

/**
 * Builds the series delete actions — "This and following events" and "All
 * events" — for an event being edited
 * on Today / Schedule / History / Activity (YAR-443), or undefined when it
 * does not apply: not a series event, no active household, or the user may
 * not delete events. `canDelete` is the SAME flag the page gates the single
 * delete on (`usePermissions().canDelete`), so the two can never disagree.
 *
 * `onDeleted` runs after a successful delete — the page closes the form and
 * refreshes exactly as it does after a single delete.
 */
export function buildSeriesDelete({
  event,
  householdId,
  canDelete,
  onDeleted,
}: {
  event: Pick<EventRow, 'series_id' | 'start_date' | 'recurrence'> | null;
  householdId: string | null | undefined;
  canDelete: boolean;
  onDeleted: () => Promise<void> | void;
}): SeriesDeleteAction | undefined {
  const seriesId = event?.series_id;
  if (!canDelete || !householdId || !event || !seriesId) return undefined;
  // A rule with no end date was approved as ONE event: nothing follows it.
  if (!seriesMayHaveFollowing(event)) return undefined;
  const start = event.start_date;
  return {
    countFollowing: () =>
      scheduleService.countThisAndFollowing(householdId, seriesId, start),
    deleteFollowing: async () => {
      await scheduleService.deleteThisAndFollowing(
        householdId,
        seriesId,
        start,
      );
      await onDeleted();
    },
    countAll: () => scheduleService.countWholeSeries(householdId, seriesId),
    deleteAll: async () => {
      await scheduleService.deleteWholeSeries(householdId, seriesId);
      await onDeleted();
    },
  };
}
